import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { monthStartLocalDate, daysFromTodayLocalDate, toDateOnlyString } from '../utils/dateOnly';
import {
  applyThousandsFormatToNumericCellsInColumn,
  xlsxSheetColWidthsFromAoa,
} from '../utils/xlsxAutoWidth';
import { formatInvoiceBoxQty, formatShipmentQtyDisplay } from '../utils/procurementInvoicePdf';

const CUSTOMER_ID = 'otoki';

interface ShipmentRow {
  shipment_date: string;
  destination_name: string;
  item_name?: string;
  bl_number?: string;
  box_qty: string | number | null;
  pack_unit?: string | null;
  weight_kg: number | null;
}

type ViewMode = 'item' | 'all' | 'bl';

interface ShippedItem {
  item_id: string;
  item_name: string;
}

interface Destination {
  id: string;
  name: string;
}

const ITEM_LIST_ROW_HEIGHT = 36;
const ITEM_LIST_MAX_VISIBLE = 10;

interface Props {
  /** OLS 고객 API 사용 시 true */
  customerMode?: boolean;
  embedded?: boolean;
}

const ProcurementShipmentListPage: React.FC<Props> = ({ customerMode = false, embedded = false }) => {
  const [start, setStart] = useState(() => monthStartLocalDate());
  const [end, setEnd] = useState(() => daysFromTodayLocalDate(7));
  const [destinationId, setDestinationId] = useState('');
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [shippedItems, setShippedItems] = useState<ShippedItem[]>([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [blSearch, setBlSearch] = useState('');
  const [itemDropdownOpen, setItemDropdownOpen] = useState(false);
  const [rows, setRows] = useState<ShipmentRow[]>([]);
  const [summary, setSummary] = useState({ totalBox: 0, totalWeight: 0 });
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('item');
  const itemSelectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = customerMode ? '' : `?customerId=${CUSTOMER_ID}`;
    fetch(`/api/procurement/destinations${q || '?customerId=' + CUSTOMER_ID}`)
      .then(async res => {
        if (res.ok) setDestinations(await res.json());
      });
  }, [customerMode]);

  const itemsBase = customerMode ? '/api/procurement/customer/shipments/items' : '/api/procurement/shipments/items';
  const listBase = customerMode ? '/api/procurement/customer/shipments' : '/api/procurement/shipments';

  const filteredItems = useMemo(() => {
    const q = itemSearch.trim().toLowerCase();
    if (!q) return shippedItems;
    return shippedItems.filter(i => i.item_name.toLowerCase().includes(q));
  }, [shippedItems, itemSearch]);

  const selectedItemName = useMemo(
    () => shippedItems.find(i => i.item_id === selectedItemId)?.item_name ?? '',
    [shippedItems, selectedItemId]
  );

  const fetchShippedItems = useCallback(async () => {
    setLoadingItems(true);
    try {
      const params = new URLSearchParams({ start, end });
      if (!customerMode) params.set('customerId', CUSTOMER_ID);
      if (destinationId) params.set('destinationId', destinationId);
      const res = await fetch(`${itemsBase}?${params}`);
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.error || '품목 목록 조회 실패');
      }
      const list: ShippedItem[] = await res.json();
      setShippedItems(list);
      if (selectedItemId && !list.some(i => i.item_id === selectedItemId)) {
        setSelectedItemId('');
        setItemSearch('');
      }
    } catch (e: any) {
      alert(e.message);
      setShippedItems([]);
      setSelectedItemId('');
      setItemSearch('');
    } finally {
      setLoadingItems(false);
    }
  }, [start, end, destinationId, customerMode, itemsBase, selectedItemId]);

  const fetchList = useCallback(async () => {
    if (viewMode === 'bl') {
      const bl = blSearch.trim();
      if (!bl) {
        setRows([]);
        setSummary({ totalBox: 0, totalWeight: 0 });
        return;
      }
    } else if (viewMode === 'item' && !selectedItemId) {
      setRows([]);
      setSummary({ totalBox: 0, totalWeight: 0 });
      return;
    }
    setLoadingRows(true);
    try {
      const params = new URLSearchParams({ start, end });
      if (!customerMode) params.set('customerId', CUSTOMER_ID);
      if (destinationId) params.set('destinationId', destinationId);
      if (viewMode === 'bl') {
        params.set('blNumber', blSearch.trim());
      } else if (viewMode === 'all') {
        params.set('allItems', 'true');
      } else {
        params.set('itemIds', selectedItemId);
      }
      const res = await fetch(`${listBase}?${params}`);
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.error || '조회 실패');
      }
      const data = await res.json();
      setRows(data.rows || []);
      setSummary(data.summary || { totalBox: 0, totalWeight: 0 });
    } catch (e: any) {
      alert(e.message);
    } finally {
      setLoadingRows(false);
    }
  }, [start, end, destinationId, selectedItemId, blSearch, viewMode, customerMode, listBase]);

  const handleSearch = async () => {
    setViewMode('item');
    setBlSearch('');
    await fetchShippedItems();
  };

  const handleBlSearch = () => {
    const bl = blSearch.trim();
    if (!bl) {
      alert('B/L 번호를 입력하세요.');
      return;
    }
    setViewMode('bl');
    setSelectedItemId('');
    setItemSearch('');
    setItemDropdownOpen(false);
  };

  const handleViewAll = () => {
    setViewMode('all');
    setSelectedItemId('');
    setItemSearch('');
    setBlSearch('');
    setItemDropdownOpen(false);
  };

  useEffect(() => {
    fetchShippedItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (itemSelectRef.current && !itemSelectRef.current.contains(e.target as Node)) {
        setItemDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const selectItem = (item: ShippedItem) => {
    setViewMode('item');
    setBlSearch('');
    setSelectedItemId(item.item_id);
    setItemDropdownOpen(false);
  };

  const handleItemSearchChange = (value: string) => {
    setItemSearch(value);
    if (shippedItems.length > 0) setItemDropdownOpen(true);
  };

  const toggleItemDropdown = () => {
    if (loadingItems || shippedItems.length === 0) return;
    setItemDropdownOpen(open => !open);
  };

  const formatDate = (d: string) => {
    const s = toDateOnlyString(d);
    const [, m, day] = s.split('-');
    return `${m}월 ${day}일`;
  };

  const formatPeriod = () => {
    const fmt = (v: string) => {
      const s = String(v).slice(0, 10);
      const [y, m, d] = s.split('-');
      return `${y.slice(2)}/${Number(m)}/${Number(d)}`;
    };
    return `${fmt(start)} ~ ${fmt(end)}`;
  };

  const sheetTitleItem = viewMode === 'bl'
    ? `B/L ${blSearch.trim()}`
    : viewMode === 'all' ? '전체' : selectedItemName;

  const showItemColumn = viewMode === 'all' || viewMode === 'bl';
  const tableColCount = showItemColumn ? 6 : 5;

  const handleExcelDownload = () => {
    if (rows.length === 0) {
      alert('다운로드할 조회 결과가 없습니다.');
      return;
    }
    const headers = showItemColumn
      ? ['날짜', 'B/L', '입고처(공장)', '품목', '출고수량', '출고중량(KG)']
      : ['날짜', 'B/L', '입고처(공장)', '출고수량', '출고중량(KG)'];
    const dataRows = rows.map(r => {
      const base: (string | number)[] = [
        toDateOnlyString(r.shipment_date),
        r.bl_number || '',
        r.destination_name,
      ];
      if (showItemColumn) base.push(r.item_name || '');
      base.push(
        r.box_qty != null && String(r.box_qty).trim() !== '' ? formatShipmentQtyDisplay(r.box_qty, r.pack_unit) : '',
        r.weight_kg != null ? Number(r.weight_kg) : '',
      );
      return base;
    });
    const totalRow: (string | number)[] = showItemColumn
      ? ['합계', '', '', '', summary.totalBox, summary.totalWeight]
      : ['합계', '', '', summary.totalBox, summary.totalWeight];

    const sheetData: (string | number)[][] = [
      [viewMode === 'bl' ? 'B/L' : '품목', sheetTitleItem],
      ['조회 기간', `${start} ~ ${end}`],
      [],
      headers,
      ...dataRows,
      totalRow,
    ];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    ws['!cols'] = xlsxSheetColWidthsFromAoa(sheetData);
    const weightColIdx = showItemColumn ? 5 : 4;
    applyThousandsFormatToNumericCellsInColumn(ws, weightColIdx);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '출고조회');
    const suffix = viewMode === 'bl'
      ? `B/L_${blSearch.trim().replace(/[\\/:*?"<>|]/g, '_')}`
      : viewMode === 'all' ? '전체' : (selectedItemName || '품목').replace(/[\\/:*?"<>|]/g, '_');
    XLSX.writeFile(wb, `출고조회_${suffix}_${start}_${end}.xlsx`);
  };

  const fieldStyle: React.CSSProperties = { padding: '6px 8px', borderRadius: 4, border: '1px solid #ccc', fontSize: 14 };

  const useFlexLayout = embedded || customerMode;
  const rootStyle: React.CSSProperties = {
    padding: customerMode ? 0 : embedded ? 0 : '12px 16px',
    height: useFlexLayout ? '100%' : undefined,
    minHeight: useFlexLayout ? 0 : undefined,
    display: useFlexLayout ? 'flex' : 'block',
    flexDirection: useFlexLayout ? 'column' : undefined,
    overflow: useFlexLayout ? 'hidden' : undefined,
    boxSizing: 'border-box',
  };

  return (
    <div style={rootStyle}>
      {!customerMode && !embedded && (
        <h2 style={{ margin: '0 0 12px', color: '#1976d2', fontSize: 22 }}>조달실 — 출고 내역 조회</h2>
      )}

      <div style={{ flexShrink: 0, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <label style={{ fontSize: 13 }}>
            조회기간
            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
              <input type="date" value={start} onChange={e => setStart(e.target.value)} style={fieldStyle} />
              <span>~</span>
              <input type="date" value={end} onChange={e => setEnd(e.target.value)} style={fieldStyle} />
            </div>
          </label>
          <label style={{ fontSize: 13 }}>
            입고처(공장)
            <select value={destinationId} onChange={e => setDestinationId(e.target.value)} style={{ ...fieldStyle, display: 'block', marginTop: 4, minWidth: 140 }}>
              <option value="">전체</option>
              {destinations.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 13 }}>
            품목 검색
            <input
              type="text"
              value={itemSearch}
              onChange={e => handleItemSearchChange(e.target.value)}
              placeholder={loadingItems ? '불러오는 중…' : shippedItems.length === 0 ? '조회 후 검색' : '품목명 검색'}
              disabled={loadingItems || shippedItems.length === 0}
              style={{ ...fieldStyle, display: 'block', marginTop: 4, width: '100%', minWidth: 160, boxSizing: 'border-box' }}
              autoComplete="off"
            />
          </label>
          <div ref={itemSelectRef} style={{ position: 'relative', fontSize: 13, minWidth: 160 }}>
            <label style={{ display: 'block' }}>
              품목 선택
              <button
                type="button"
                onClick={toggleItemDropdown}
                disabled={loadingItems || shippedItems.length === 0}
                style={{
                  ...fieldStyle,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: 4,
                  width: '100%',
                  minWidth: 160,
                  boxSizing: 'border-box',
                  background: loadingItems || shippedItems.length === 0 ? '#f5f5f5' : '#fff',
                  cursor: loadingItems || shippedItems.length === 0 ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  color: selectedItemName ? '#111' : '#888',
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {loadingItems ? '불러오는 중…' : selectedItemName || '선택하세요'}
                </span>
                <span style={{ marginLeft: 8, fontSize: 10, color: '#666' }}>▼</span>
              </button>
            </label>
            {itemDropdownOpen && !loadingItems && shippedItems.length > 0 && (
              <ul
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  margin: '4px 0 0',
                  padding: 0,
                  listStyle: 'none',
                  background: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  maxHeight: ITEM_LIST_ROW_HEIGHT * ITEM_LIST_MAX_VISIBLE,
                  overflowY: 'auto',
                  zIndex: 20,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                }}
              >
                {filteredItems.length === 0 ? (
                  <li style={{ padding: '10px 12px', color: '#888', fontSize: 13 }}>검색 결과 없음</li>
                ) : (
                  filteredItems.map(item => (
                    <li
                      key={item.item_id}
                      role="option"
                      aria-selected={selectedItemId === item.item_id}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => selectItem(item)}
                      style={{
                        padding: '8px 12px',
                        minHeight: ITEM_LIST_ROW_HEIGHT,
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        fontSize: 13,
                        background: selectedItemId === item.item_id ? '#e3f2fd' : '#fff',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      {item.item_name}
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
          <button type="button" onClick={handleSearch} disabled={loadingItems} style={{ padding: '8px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
            {loadingItems ? '조회 중…' : '조회'}
          </button>
          <button
            type="button"
            onClick={handleViewAll}
            disabled={loadingRows}
            style={{
              padding: '8px 16px',
              background: viewMode === 'all' ? '#0d47a1' : '#546e7a',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: loadingRows ? 'not-allowed' : 'pointer',
              fontWeight: 600,
            }}
          >
            {loadingRows && viewMode === 'all' ? '불러오는 중…' : '모든 내역 보기'}
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
          <label style={{ fontSize: 13 }}>
            B/L 번호
            <input
              type="text"
              value={blSearch}
              onChange={e => setBlSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleBlSearch(); }}
              placeholder="B/L 번호 입력"
              style={{ ...fieldStyle, display: 'block', marginTop: 4, width: '100%', minWidth: 200, boxSizing: 'border-box' }}
              autoComplete="off"
            />
          </label>
          <button type="button" onClick={handleBlSearch} disabled={loadingRows} style={{ padding: '8px 16px', background: viewMode === 'bl' ? '#0d47a1' : '#5c6bc0', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
            B/L 조회
          </button>
        </div>
      </div>

      {(viewMode === 'all' || viewMode === 'bl' || selectedItemName || rows.length > 0) && (
        <div style={{ flexShrink: 0, marginBottom: 10, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
            <div><strong>{viewMode === 'bl' ? 'B/L' : '품목'}:</strong> {sheetTitleItem || '—'}</div>
            <div><strong>조회 기간:</strong> {formatPeriod()}</div>
          </div>
          {rows.length > 0 && (
            <button
              type="button"
              onClick={handleExcelDownload}
              disabled={loadingRows}
              style={{
                padding: '8px 16px',
                background: '#2e7d32',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: loadingRows ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              엑셀 다운로드
            </button>
          )}
        </div>
      )}

      <div style={{
        flex: useFlexLayout ? 1 : undefined,
        minHeight: useFlexLayout ? 0 : undefined,
        overflow: useFlexLayout ? 'hidden' : undefined,
        display: 'flex',
        flexDirection: 'column',
      }}>
      {loadingRows ? (
        <div style={{ padding: 24, color: '#888' }}>내역 조회 중…</div>
      ) : viewMode === 'item' && !selectedItemId ? (
        <div style={{ padding: 24, color: '#888', textAlign: 'center', border: '1px dashed #ccc', borderRadius: 8 }}>
          {shippedItems.length === 0
            ? '조회 버튼을 눌러 기간 내 출고 품목을 불러오세요.'
            : '품목을 선택하거나 B/L 번호로 조회하거나 「모든 내역 보기」를 눌러주세요.'}
        </div>
      ) : viewMode === 'bl' && !blSearch.trim() ? (
        <div style={{ padding: 24, color: '#888', textAlign: 'center', border: '1px dashed #ccc', borderRadius: 8 }}>
          B/L 번호를 입력한 뒤 「B/L 조회」를 눌러주세요.
        </div>
      ) : (
        <div style={{
          flex: useFlexLayout ? 1 : undefined,
          minHeight: useFlexLayout ? 0 : undefined,
          overflow: 'auto',
          border: '1px solid #ddd',
          borderRadius: 8,
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f5f7fa', position: useFlexLayout ? 'sticky' : undefined, top: useFlexLayout ? 0 : undefined, zIndex: useFlexLayout ? 1 : undefined }}>
                <th style={{ border: '1px solid #ddd', padding: 8, background: '#f5f7fa' }}>날짜</th>
                <th style={{ border: '1px solid #ddd', padding: 8, background: '#f5f7fa' }}>B/L</th>
                <th style={{ border: '1px solid #ddd', padding: 8, background: '#f5f7fa' }}>입고처(공장)</th>
                {showItemColumn && (
                  <th style={{ border: '1px solid #ddd', padding: 8, background: '#f5f7fa' }}>품목</th>
                )}
                <th style={{ border: '1px solid #ddd', padding: 8, background: '#f5f7fa' }}>출고수량</th>
                <th style={{ border: '1px solid #ddd', padding: 8, background: '#f5f7fa' }}>출고중량(KG)</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={tableColCount} style={{ textAlign: 'center', padding: 24, color: '#888' }}>내역 없음</td></tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={idx}>
                    <td style={{ border: '1px solid #eee', padding: 8, textAlign: 'center' }}>{formatDate(r.shipment_date)}</td>
                    <td style={{ border: '1px solid #eee', padding: 8, textAlign: 'center' }}>{r.bl_number || ''}</td>
                    <td style={{ border: '1px solid #eee', padding: 8, textAlign: 'center' }}>{r.destination_name}</td>
                    {showItemColumn && (
                      <td style={{ border: '1px solid #eee', padding: 8, textAlign: 'center' }}>{r.item_name || ''}</td>
                    )}
                    <td style={{ border: '1px solid #eee', padding: 8, textAlign: 'center' }}>{formatShipmentQtyDisplay(r.box_qty, r.pack_unit)}</td>
                    <td style={{ border: '1px solid #eee', padding: 8, textAlign: 'right' }}>{r.weight_kg != null ? Number(r.weight_kg).toLocaleString() : ''}</td>
                  </tr>
                ))
              )}
              {rows.length > 0 && (
                <tr style={{ background: '#f0f4f8', fontWeight: 700 }}>
                  <td colSpan={showItemColumn ? 4 : 3} style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>합계</td>
                  <td style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>{summary.totalBox.toLocaleString()}</td>
                  <td style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>{summary.totalWeight.toLocaleString()}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </div>
  );
};

export default ProcurementShipmentListPage;
